import { zodTransformFirestoreTimestampToDate } from "../utils/zodUtils.mjs";
import z from "zod";

// src/models/classNotification.ts
/** Reminder offsets before class start (product enum). */
const CLASS_NOTIFICATION_INTERVALS = [
	"15m",
	"30m",
	"1h"
];
const classNotificationIntervalSchema = z.enum(CLASS_NOTIFICATION_INTERVALS);
const INTERVAL_SET = new Set(CLASS_NOTIFICATION_INTERVALS);
function filterValidIntervals(value) {
	if (!Array.isArray(value)) return [];
	const out = [];
	for (const x of value) if (typeof x === "string" && INTERVAL_SET.has(x)) out.push(x);
	return out;
}
const CLASS_NOTIFICATION_INTERVAL_MINUTES = {
	"15m": 15,
	"30m": 30,
	"1h": 60
};
const CLASS_NOTIFICATION_INTERVAL_LABELS = {
	"15m": "15 minutes",
	"30m": "30 minutes",
	"1h": "1 hour"
};
const classNotificationChannelConfigInnerSchema = z.object({
	enabled: z.boolean(),
	intervals: z.array(classNotificationIntervalSchema).min(1).max(3)
});
/**
* Enrollment `notificationConfig`: per channel `enabled` + `intervals` (no class_schedules copy).
*/
const DEFAULT_SCHEDULE_NOTIFICATION_CONFIG = {
	fcm: {
		enabled: false,
		intervals: ["15m"]
	},
	sms: {
		enabled: false,
		intervals: ["15m"]
	}
};
/** Enrollment defaults with SMS mirroring account-level `classReminderGlobals.sms`. */
function enrollmentNotificationConfigFromGlobalSms(globalSmsEnabled) {
	return {
		fcm: {
			...DEFAULT_SCHEDULE_NOTIFICATION_CONFIG.fcm,
			intervals: [...DEFAULT_SCHEDULE_NOTIFICATION_CONFIG.fcm.intervals]
		},
		sms: {
			enabled: globalSmsEnabled,
			intervals: [...DEFAULT_SCHEDULE_NOTIFICATION_CONFIG.sms.intervals]
		}
	};
}
function isNestedChannelShape(value) {
	return value !== null && typeof value === "object" && !Array.isArray(value) && "enabled" in value && "intervals" in value;
}
function normalizeNestedPartial(raw) {
	const def = DEFAULT_SCHEDULE_NOTIFICATION_CONFIG;
	const fRaw = raw.fcm;
	const sRaw = raw.sms;
	const f = isNestedChannelShape(fRaw) ? fRaw : {
		intervals: [],
		enabled: false
	};
	const s = isNestedChannelShape(sRaw) ? sRaw : {
		intervals: [],
		enabled: false
	};
	const fIntervals = filterValidIntervals(f.intervals);
	const sIntervals = filterValidIntervals(s.intervals);
	return {
		fcm: {
			enabled: Boolean(f.enabled),
			intervals: fIntervals.length > 0 ? fIntervals : [...def.fcm.intervals]
		},
		sms: {
			enabled: Boolean(s.enabled),
			intervals: sIntervals.length > 0 ? sIntervals : [...def.sms.intervals]
		}
	};
}
function normalizeLegacyFlat(raw) {
	const fcmBool = Boolean(raw.fcm);
	const smsBool = Boolean(raw.sms);
	const shared = filterValidIntervals(raw.intervals);
	const fcmOnly = filterValidIntervals(raw.intervalsFcm);
	const smsOnly = filterValidIntervals(raw.intervalsSms);
	const fcmIntervals = fcmOnly.length > 0 ? [fcmOnly[0]] : shared.length > 0 ? [...shared] : [...DEFAULT_SCHEDULE_NOTIFICATION_CONFIG.fcm.intervals];
	const smsIntervals = smsOnly.length > 0 ? [smsOnly[0]] : shared.length > 0 ? [...shared] : [...DEFAULT_SCHEDULE_NOTIFICATION_CONFIG.sms.intervals];
	return {
		fcm: {
			enabled: fcmBool,
			intervals: fcmIntervals.length > 0 ? fcmIntervals : ["15m"]
		},
		sms: {
			enabled: smsBool,
			intervals: smsIntervals.length > 0 ? smsIntervals : ["15m"]
		}
	};
}
function normalizeClassScheduleNotificationConfigInput(raw) {
	if (raw === null || raw === void 0) return {
		fcm: { ...DEFAULT_SCHEDULE_NOTIFICATION_CONFIG.fcm },
		sms: { ...DEFAULT_SCHEDULE_NOTIFICATION_CONFIG.sms }
	};
	if (typeof raw !== "object" || Array.isArray(raw)) return {
		fcm: { ...DEFAULT_SCHEDULE_NOTIFICATION_CONFIG.fcm },
		sms: { ...DEFAULT_SCHEDULE_NOTIFICATION_CONFIG.sms }
	};
	const o = raw;
	if (isNestedChannelShape(o.fcm) || isNestedChannelShape(o.sms)) return normalizeNestedPartial(o);
	return normalizeLegacyFlat(o);
}
const classScheduleNotificationConfigInnerSchema = z.object({
	fcm: classNotificationChannelConfigInnerSchema,
	sms: classNotificationChannelConfigInnerSchema
});
const classScheduleNotificationConfigSchema = z.preprocess((raw) => normalizeClassScheduleNotificationConfigInput(raw), classScheduleNotificationConfigInnerSchema);
function parseClassScheduleNotificationConfig(raw) {
	return classScheduleNotificationConfigSchema.parse(raw === void 0 || raw === null ? void 0 : raw);
}
/**
* Effective FCM reminder intervals when the channel is enabled.
*/
function effectiveEnrollmentIntervalsFcm(cfg) {
	return cfg.fcm.enabled ? [...cfg.fcm.intervals] : [];
}
/**
* Effective SMS reminder intervals when the channel is enabled.
*/
function effectiveEnrollmentIntervalsSms(cfg) {
	return cfg.sms.enabled ? [...cfg.sms.intervals] : [];
}
const joiningNotificationsScheduledEntrySchema = z.object({ scheduledAt: z.any().transform(zodTransformFirestoreTimestampToDate).pipe(z.date()) });
const joiningNotificationsScheduledObjectSchema = z.object({
	"15m": joiningNotificationsScheduledEntrySchema.optional(),
	"30m": joiningNotificationsScheduledEntrySchema.optional(),
	"1h": joiningNotificationsScheduledEntrySchema.optional()
});
/**
* Sparse per-interval schedule flags. Absent / null / undefined field, or keys with
* undefined values (common after Firestore dot-notation updates), are all valid.
*/
const joiningNotificationsScheduledSchema = z.preprocess((value) => {
	if (value === void 0 || value === null) return;
	if (typeof value !== "object" || Array.isArray(value)) return value;
	return Object.fromEntries(Object.entries(value).filter(([key, entry]) => entry != null && classNotificationIntervalSchema.safeParse(key).success));
}, joiningNotificationsScheduledObjectSchema.optional());

export { CLASS_NOTIFICATION_INTERVALS, CLASS_NOTIFICATION_INTERVAL_LABELS, CLASS_NOTIFICATION_INTERVAL_MINUTES, DEFAULT_SCHEDULE_NOTIFICATION_CONFIG, classNotificationIntervalSchema, classScheduleNotificationConfigSchema, effectiveEnrollmentIntervalsFcm, effectiveEnrollmentIntervalsSms, enrollmentNotificationConfigFromGlobalSms, joiningNotificationsScheduledEntrySchema, joiningNotificationsScheduledSchema, normalizeClassScheduleNotificationConfigInput, parseClassScheduleNotificationConfig };
//# sourceMappingURL=classNotification.mjs.map